import { ChangeDetectionStrategy, Component, computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MAT_SNACK_BAR_DATA, MatSnackBar, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar'

/**
 * Die vier Meldungsarten. `warn` ist bewusst nicht dasselbe wie `error`: eine Warnung sagt
 * "das ist durchgelaufen, sieh es dir aber an", ein Fehler "das ist nicht passiert".
 */
export type SnackbarKind = 'success' | 'info' | 'warn' | 'error'

/**
 * Ein einzelner Knopf in der Meldung — das Angebot, das Getane zurueckzunehmen oder gleich
 * weiterzugehen.
 *
 * Gedacht fuer den Fall, in dem eine Aktion ohne Rueckfrage durchlaeuft und die Rueckfrage
 * stattdessen HINTERHER angeboten wird (Vorbild: der Ausstieg aus dem HiMi-Assistenten, der den
 * Vorgang still als Entwurf parkt und hier „Verwerfen" anbietet). Der Knopf ist damit die
 * Alternative zu einem Dialog, nicht seine Ergaenzung.
 */
export interface SnackbarAktion {
  /** Beschriftung. Ein Verb, kurz — sie steht neben dem Meldungstext und darf ihn nicht erschlagen. */
  readonly label: string
  /**
   * Was der Klick tut. Die Meldung schliesst sich davor von selbst.
   *
   * Laeuft haeufig, NACHDEM die ausloesende Komponente zerstoert ist (der Nutzer hat die Seite ja
   * gerade verlassen) — also nur mit Werten und app-weiten Services arbeiten, nie mit Signalen der
   * alten Komponente.
   */
  readonly run: () => void
}

export interface SnackbarData {
  kind: SnackbarKind
  message: string
  /** Zaehlt hoch, wenn dieselbe Meldung erneut angefordert wird — siehe Entdopplung im Service. */
  count: Signal<number>
  action?: SnackbarAktion
}

const ICONS: Record<SnackbarKind, string> = {
  success: 'check_circle',
  info: 'info',
  warn: 'warning',
  error: 'error',
}

/**
 * Screenreader lesen nur den Text vor — ohne dieses Praefix klingen "gespeichert" und
 * "konnte nicht gespeichert werden" gleich dringlich, weil Farbe und Icon dort nicht ankommen.
 */
const SCREENREADER_PREFIX: Record<SnackbarKind, string> = {
  success: 'Erfolg',
  info: 'Hinweis',
  warn: 'Warnung',
  error: 'Fehler',
}

@Component({
  selector: 'lib-snackbar',
  template: `
    <span matSnackBarLabel class="snackbar-label">
      <mat-icon class="snackbar-icon" aria-hidden="true">{{ icon() }}</mat-icon>
      <span class="snackbar-text">
        <span class="cdk-visually-hidden">{{ screenreaderPrefix() }}:</span>
        {{ data.message }}
      </span>
      @if (data.count() > 1) {
        <span class="snackbar-count" [attr.aria-label]="data.count() + ' Mal aufgetreten'">{{ data.count() }}&times;</span>
      }
    </span>
    <span matSnackBarActions>
      @if (data.action; as aktion) {
        <button type="button" matButton matSnackBarAction class="snackbar-aktion" (click)="aktionAusfuehren(aktion)">
          {{ aktion.label }}
        </button>
      }
      <!--
        Nur der Fehler bekommt ein X. Alles andere verschwindet nach seiner Standzeit von selbst
        (siehe MIN_DURATION_MS), und ein Schliessen-Knopf an einer Meldung, die ohnehin geht,
        behauptet eine noetige Handlung, wo keine ist. Bis 2026-08-31 stand er an jeder Meldung.
      -->
      @if (data.kind === 'error') {
        <button
          type="button"
          matIconButton
          matSnackBarAction
          class="snackbar-close"
          aria-label="Meldung schließen"
          (click)="snackbarRef.dismiss()"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
    </span>
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
    }
    .snackbar-label {
      display: flex;
      /* Mittig, nicht oben: die allermeisten Meldungen sind einzeilig, und dort sass das Icon
         sichtbar zu hoch. Bei zwei Zeilen steht es damit auf halber Hoehe des Blocks, was hier
         besser aussieht als an der ersten Zeile zu kleben. Mittig macht die Kastenhoehe des
         Icons ausserdem egal (siehe die Icon-Regel in der Repo-CLAUDE.md). */
      align-items: center;
      gap: 0.625rem;
      /* Lange Meldungen umbrechen statt zu kappen — ein halber Fehlertext ist keiner. */
      min-width: 0;
    }
    .snackbar-icon {
      /* Die Statusfarbe steckt NUR hier (siehe .lib-snackbar in basic.scss) — die Flaeche selbst
         bleibt neutral, und eine farbige Kante links gibt es bewusst nicht mehr. */
      color: var(--lib-snackbar-accent, currentColor);
      flex: none;
      font-size: 1.25rem;
      width: 1.25rem;
      height: 1.25rem;
    }
    .snackbar-text {
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .snackbar-count {
      flex: none;
      align-self: center;
      padding: 0.05rem 0.375rem;
      border-radius: 0.6rem;
      background-color: var(--mat-sys-surface-container-high);
      color: var(--mat-sys-on-surface-variant);
      font-size: 0.75rem;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }
    .snackbar-aktion {
      /* Das Angebot traegt die Primaerfarbe, damit es sich vom Meldungstext abhebt; die Flaeche
         bleibt neutral wie der Rest der Karte. */
      --mat-button-text-label-text-color: var(--mat-sys-primary);
      flex: none;
    }
    .snackbar-close {
      /* Materials 40px-Icon-Button ueberstrahlt auf der schlanken Karte die Meldung selbst. */
      flex: none;
      width: 1.75rem;
      height: 1.75rem;
      padding: 0;
      margin-right: 0.375rem;

      .mat-icon {
        font-size: 1.125rem;
        width: 1.125rem;
        height: 1.125rem;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSnackBarModule, MatButtonModule, MatIconModule],
})
export class SnackbarComponent {
  readonly snackbarRef = inject(MatSnackBarRef)
  readonly data: SnackbarData = inject(MAT_SNACK_BAR_DATA)

  readonly icon = computed(() => ICONS[this.data.kind])
  readonly screenreaderPrefix = computed(() => SCREENREADER_PREFIX[this.data.kind])

  /** Erst schliessen, dann handeln: die Meldung ist erledigt, sobald jemand ihr Angebot annimmt. */
  aktionAusfuehren(aktion: SnackbarAktion) {
    this.snackbarRef.dismissWithAction()
    aktion.run()
  }
}

interface QueueItem {
  kind: SnackbarKind
  message: string
  count: WritableSignal<number>
  action?: SnackbarAktion
}

/** Mindest-Standzeit je Art. Ein Fehler steht, bis er weggeklickt wird (siehe `ERROR_HANDOVER_MS`). */
const MIN_DURATION_MS: Record<SnackbarKind, number> = {
  success: 3500,
  info: 4000,
  warn: 5500,
  error: 0,
}

/** Lesezeit-Zuschlag: Erfassen der Meldung + rund 180 Woerter/Minute. */
const RECOGNITION_MS = 1200
const MS_PER_WORD = 330
const MAX_DURATION_MS = 12000

/**
 * Ein Fehler bleibt stehen — aber nicht auf Kosten aller anderen Meldungen. Wartet etwas hinter
 * ihm, raeumt er nach dieser Zeit das Feld. Ohne das Ventil blockiert eine einzige unbeachtete
 * Fehlermeldung die Warteschlange dauerhaft.
 */
const ERROR_HANDOVER_MS = 15000

/** Mehr als das wartet niemand ab; ueberzaehlige Meldungen niedrigerer Dringlichkeit fallen raus. */
const MAX_QUEUE = 4

/**
 * Mit Angebot braucht die Meldung Zeit zum Lesen UND zum Entscheiden. Der Wert ist die Untergrenze;
 * eine lange Meldung steht ueber die Lesezeit ohnehin laenger.
 */
const ACTION_MIN_DURATION_MS = 9000

function durationMs(item: QueueItem): number {
  if (item.kind === 'error') return 0
  const words = item.message.split(/\s+/).length
  const gelesen = Math.max(MIN_DURATION_MS[item.kind], RECOGNITION_MS + words * MS_PER_WORD)
  return Math.min(MAX_DURATION_MS, item.action ? Math.max(gelesen, ACTION_MIN_DURATION_MS) : gelesen)
}

/**
 * Der einzige Kanal fuer kurze Rueckmeldungen an die Nutzer:in.
 *
 * Warum der Service eine eigene Warteschlange fuehrt: `MatSnackBar.open()` **verdraengt** eine
 * offene Meldung, es reiht sie nicht ein (`_openedSnackBarRef.dismiss()` in Materials Quelle).
 * Zehn Fehler aus einer Massenaktion waren damit ein Flackern, an dessen Ende genau der letzte
 * lesbar stehen blieb. Deshalb oeffnet dieser Service nie, solange etwas steht, und macht aus
 * Wiederholungen derselben Meldung einen Zaehler statt einer Wiederholung.
 */
@Injectable()
export class SnackbarService {
  readonly matSnackBar = inject(MatSnackBar)

  private readonly queue: QueueItem[] = []
  private current?: { item: QueueItem; ref: MatSnackBarRef<SnackbarComponent> }
  private handoverTimer?: ReturnType<typeof setTimeout>

  /** Etwas hat geklappt: gespeichert, gesendet, geloescht. */
  showSuccess(message: string, action?: SnackbarAktion) {
    this.show('success', message, action)
  }

  /** Sachstand ohne Wertung — laeuft, wurde angestossen, ist unterwegs. */
  showInfo(message: string, action?: SnackbarAktion) {
    this.show('info', message, action)
  }

  /** Durchgelaufen, aber mit einem Vorbehalt, den die Nutzer:in kennen muss. */
  showWarning(message: string, action?: SnackbarAktion) {
    this.show('warn', message, action)
  }

  /** Nicht passiert. Bleibt stehen, bis sie weggeklickt wird. */
  showError(message: string, action?: SnackbarAktion) {
    this.show('error', message, action)
  }

  /** Raeumt Sichtbares und Wartendes ab — z.B. bevor ein Ablauf von vorn beginnt. */
  dismissAll() {
    this.queue.length = 0
    this.clearHandover()
    this.matSnackBar.dismiss()
  }

  private show(kind: SnackbarKind, message: string, action?: SnackbarAktion) {
    const text = message?.trim()
    if (!text) return

    // Entdopplung: dieselbe Meldung, die schon steht oder wartet, zaehlt hoch. Eine Meldung MIT
    // Angebot bleibt davon ausgenommen — sie gehoert zu genau einem Vorgang, und ein Zaehler „2x"
    // an einem „Verwerfen" liesse offen, welchen der beiden es trifft.
    const known = action ? undefined : this.findSame(kind, text)
    if (known) {
      known.count.update((n) => n + 1)
      this.scheduleHandover()
      return
    }

    const item: QueueItem = { kind, message: text, count: signal(1), action }

    if (!this.current) {
      this.open(item)
      return
    }

    // Ein Fehler ueberholt alles, was noch wartet — aber nie einen anderen Fehler.
    const insertAt = kind === 'error' ? this.queue.findIndex((q) => q.kind !== 'error') : -1
    if (insertAt >= 0) {
      this.queue.splice(insertAt, 0, item)
    } else {
      this.queue.push(item)
    }

    this.trimQueue()
    this.scheduleHandover()
  }

  private findSame(kind: SnackbarKind, message: string): QueueItem | undefined {
    if (this.current?.item.kind === kind && this.current.item.message === message && !this.current.item.action) return this.current.item
    return this.queue.find((q) => q.kind === kind && q.message === message && !q.action)
  }

  private trimQueue() {
    while (this.queue.length > MAX_QUEUE) {
      const droppable = this.queue.findIndex((q) => q.kind !== 'error')
      this.queue.splice(droppable >= 0 ? droppable : 0, 1)
    }
  }

  private open(item: QueueItem) {
    const ref = this.matSnackBar.openFromComponent(SnackbarComponent, {
      data: <SnackbarData>{ kind: item.kind, message: item.message, count: item.count.asReadonly(), action: item.action },
      duration: durationMs(item),
      // Fehler und Warnungen unterbrechen den Screenreader, alles andere wartet auf eine Pause.
      politeness: item.kind === 'error' || item.kind === 'warn' ? 'assertive' : 'polite',
      panelClass: ['lib-snackbar', `lib-snackbar-${item.kind}`],
    })

    this.current = { item, ref }

    ref.afterDismissed().subscribe(() => {
      this.current = undefined
      this.clearHandover()
      const next = this.queue.shift()
      if (next) this.open(next)
    })
  }

  /** Siehe `ERROR_HANDOVER_MS` — greift nur, wenn ein stehender Fehler etwas aufhaelt. */
  private scheduleHandover() {
    if (this.handoverTimer || this.current?.item.kind !== 'error' || this.queue.length === 0) return
    this.handoverTimer = setTimeout(() => this.current?.ref.dismiss(), ERROR_HANDOVER_MS)
  }

  private clearHandover() {
    clearTimeout(this.handoverTimer)
    this.handoverTimer = undefined
  }
}
